import React, { useEffect, useState } from 'react'
import { supabase, parseRpcError } from '@/lib/supabaseClient'
import type { CompanyPortalRow, PortalStatus, TemporaryUpdateCodeRow } from '@/lib/types'
import { Badge, Button, Card, Checkbox, Field, Input, Modal, PortalStatusBadge, Select, useConfirm, useToast } from '@/components/ui'
import { companyPortalUrl } from '@/lib/env'
import { formatDateTime, fromDatetimeLocalInTz, toDatetimeLocalInTz } from '@/lib/format'

export default function AccessTab({ companyId }: { companyId: string }) {
  const [portal, setPortal] = useState<CompanyPortalRow | null>(null)
  const [codes, setCodes] = useState<TemporaryUpdateCodeRow[]>([])
  const [showCodeModal, setShowCodeModal] = useState(false)
  const [revealedCode, setRevealedCode] = useState<string | null>(null)
  const [revealedLink, setRevealedLink] = useState<string | null>(null)
  const [customCodeInput, setCustomCodeInput] = useState('')
  const { push } = useToast()
  const { confirm, confirmDialog } = useConfirm()

  async function load() {
    const [{ data: p }, { data: c }] = await Promise.all([
      supabase.from('company_portals').select('*').eq('company_id', companyId).maybeSingle(),
      supabase.from('temporary_update_codes').select('*').eq('company_id', companyId).order('created_at', { ascending: false }),
    ])
    setPortal(p as CompanyPortalRow)
    setCodes((c as TemporaryUpdateCodeRow[]) || [])
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId])

  if (!portal) return null

  async function setStatus(status: PortalStatus) {
    const { error } = await supabase.rpc('admin_set_portal_status', { p_company_id: companyId, p_status: status })
    if (error) push(parseRpcError(error).code, 'error')
    else {
      push(`Portal status set to ${status}`)
      load()
    }
  }

  async function saveDeadline(deadlineLocal: string, autoClose: boolean) {
    if (!portal) return
    const iso = deadlineLocal ? fromDatetimeLocalInTz(deadlineLocal, portal.deadline_timezone) : null
    const { error } = await supabase.from('company_portals').update({ deadline_at: iso, deadline_auto_close: autoClose }).eq('company_id', companyId)
    if (error) push(error.message, 'error')
    else {
      push('Deadline updated')
      load()
    }
  }

  async function toggleAccessCodeRequired(required: boolean) {
    const { error } = await supabase.from('company_portals').update({ require_access_code: required }).eq('company_id', companyId)
    if (error) push(error.message, 'error')
    else load()
  }

  async function toggleRequireSubmission(required: boolean) {
    const { error } = await supabase.from('company_portals').update({ require_final_submission: required }).eq('company_id', companyId)
    if (error) push(error.message, 'error')
    else load()
  }

  async function toggleShowPhysical(v: boolean | null) {
    const { error } = await supabase.from('company_portals').update({ show_physical_room_numbers: v }).eq('company_id', companyId)
    if (error) push(error.message, 'error')
    else load()
  }

  async function regenerateLink() {
    const ok = await confirm({ title: 'Regenerate portal link?', description: 'The previous link will stop working immediately.', tone: 'danger', confirmLabel: 'Regenerate' })
    if (!ok) return
    const { data, error } = await supabase.rpc('admin_regenerate_portal_link', { p_company_id: companyId })
    if (error) {
      push(parseRpcError(error).code, 'error')
      return
    }
    setRevealedLink(companyPortalUrl(data as string))
    load()
  }

  async function resetAccessCode(customCode?: string) {
    const { data, error } = await supabase.rpc('admin_reset_access_code', { p_company_id: companyId, p_new_code: customCode?.trim() || null })
    if (error) {
      push(parseRpcError(error).code, 'error')
      return
    }
    setRevealedCode(data as string)
    setCustomCodeInput('')
    load()
  }

  async function copyLink() {
    if (!portal) return
    await navigator.clipboard.writeText(companyPortalUrl(portal.portal_token))
    push('Link copied')
  }

  async function copyPin(pin: string) {
    await navigator.clipboard.writeText(pin)
    push('PIN copied')
  }

  async function copyLinkAndPin() {
    if (!portal || !portal.access_code_plain) return
    await navigator.clipboard.writeText(`${companyPortalUrl(portal.portal_token)}\nAccess code: ${portal.access_code_plain}`)
    push('Link + PIN copied')
  }

  async function revokeCode(codeId: string) {
    const { error } = await supabase.rpc('admin_revoke_update_code', { p_update_code_id: codeId })
    if (error) push(parseRpcError(error).code, 'error')
    else {
      push('Update code revoked')
      load()
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      <Card className="p-5">
        <h3 className="mb-3 text-sm font-semibold text-slate-900">Portal Link</h3>
        <div className="flex items-center gap-2">
          <Input readOnly value={companyPortalUrl(portal.portal_token)} className="text-xs" />
          <Button size="sm" onClick={copyLink}>
            Copy Link
          </Button>
        </div>

        <div className="mt-4 border-t border-slate-100 pt-4">
          <Checkbox label="Require access code (PIN)" checked={portal.require_access_code} onChange={(e) => toggleAccessCodeRequired(e.target.checked)} />

          {portal.require_access_code && (
            <div className="mt-3 rounded-md bg-slate-50 p-3">
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-400">Current PIN</div>
              {portal.access_code_plain ? (
                <div className="flex items-center gap-2">
                  <div className="rounded border border-slate-200 bg-white px-3 py-1.5 font-mono text-base tracking-wider text-slate-900">{portal.access_code_plain}</div>
                  <Button size="sm" variant="secondary" onClick={() => copyPin(portal.access_code_plain!)}>
                    Copy PIN
                  </Button>
                  <Button size="sm" variant="secondary" onClick={copyLinkAndPin}>
                    Copy Link + PIN
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-slate-500">A PIN was set before this feature existed, so it can't be shown — click below to set a new one.</p>
              )}
            </div>
          )}

          <div className="mt-3 flex items-center gap-2">
            <Input
              value={customCodeInput}
              onChange={(e) => setCustomCodeInput(e.target.value)}
              placeholder="Choose your own PIN (optional)"
              className="max-w-[220px] text-sm"
            />
            <Button size="sm" variant="secondary" onClick={() => resetAccessCode(customCodeInput)}>
              {customCodeInput.trim() ? 'Set This PIN' : 'Generate Random PIN'}
            </Button>
          </div>
          <p className="mt-1 text-xs text-slate-400">Leave the box empty and click the button for a random PIN, or type your own (e.g. company name + numbers) to set it.</p>
        </div>

        <Button size="sm" variant="ghost" className="mt-4 text-red-600" onClick={regenerateLink}>
          Regenerate Link (invalidates the old one)
        </Button>
      </Card>

      <Card className="p-5">
        <h3 className="mb-3 text-sm font-semibold text-slate-900">Status & Deadline</h3>
        <div className="mb-3 flex items-center gap-2">
          <span className="text-sm text-slate-600">Current status:</span>
          <PortalStatusBadge status={portal.status} />
        </div>
        <div className="mb-4 flex flex-wrap gap-2">
          {(['open', 'edit_only', 'closed'] as PortalStatus[]).map((s) => (
            <Button key={s} size="sm" variant={portal.status === s ? 'primary' : 'secondary'} onClick={() => setStatus(s)}>
              Set {s.replace('_', ' ')}
            </Button>
          ))}
        </div>
        <DeadlineForm portal={portal} onSave={saveDeadline} />
        <div className="mt-3 space-y-2">
          <Checkbox label="Require final submission" checked={portal.require_final_submission} onChange={(e) => toggleRequireSubmission(e.target.checked)} />
          <div>
            <span className="mb-1 block text-sm text-slate-700">Show physical room numbers to company</span>
            <Select
              className="max-w-xs"
              value={portal.show_physical_room_numbers === null ? 'default' : portal.show_physical_room_numbers ? 'yes' : 'no'}
              onChange={(e) => toggleShowPhysical(e.target.value === 'default' ? null : e.target.value === 'yes')}
            >
              <option value="default">Use event default</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">Temporary Update Codes</h3>
          <Button size="sm" variant="primary" onClick={() => setShowCodeModal(true)}>
            Generate Update Code
          </Button>
        </div>
        {codes.length === 0 ? (
          <p className="text-sm text-slate-500">None generated yet. Use this to grant limited editing access after the deadline has passed.</p>
        ) : (
          <div className="space-y-1.5 text-sm">
            {codes.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded border border-slate-100 px-3 py-2">
                <div>
                  <div className="text-slate-700">Expires {formatDateTime(c.expires_at, portal.deadline_timezone)}</div>
                  <div className="text-xs text-slate-400">
                    {c.permission_edit_existing && 'Edit '}
                    {c.permission_add_to_empty && 'Add '}
                    {c.permission_delete_guests && 'Delete '}
                    · used {c.uses_count}
                    {c.max_uses ? `/${c.max_uses}` : ''}
                  </div>
                </div>
                {c.is_active ? (
                  <Button size="sm" variant="ghost" onClick={() => revokeCode(c.id)}>
                    Revoke
                  </Button>
                ) : (
                  <Badge tone="slate">Revoked</Badge>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {showCodeModal && (
        <GenerateCodeModal
          companyId={companyId}
          onClose={() => setShowCodeModal(false)}
          onGenerated={(code) => {
            setShowCodeModal(false)
            setRevealedCode(code)
            load()
          }}
        />
      )}

      {revealedCode && (
        <Modal open onClose={() => setRevealedCode(null)} title="Copy this now">
          <p className="text-sm text-slate-600">This value is only shown once — copy it before closing this dialog.</p>
          <div className="mt-3 rounded border border-slate-200 bg-slate-50 p-3 text-center font-mono text-lg tracking-wide">{revealedCode}</div>
          <Button
            className="mt-3 w-full"
            onClick={() => {
              navigator.clipboard.writeText(revealedCode)
              push('Copied')
            }}
          >
            Copy
          </Button>
        </Modal>
      )}
      {revealedLink && (
        <Modal open onClose={() => setRevealedLink(null)} title="New portal link">
          <p className="text-sm text-slate-600">Send this new link to the company — the old one no longer works.</p>
          <div className="mt-3 break-all rounded border border-slate-200 bg-slate-50 p-3 text-sm">{revealedLink}</div>
          <Button
            className="mt-3 w-full"
            onClick={() => {
              navigator.clipboard.writeText(revealedLink)
              push('Copied')
            }}
          >
            Copy
          </Button>
        </Modal>
      )}
      {confirmDialog}
    </div>
  )
}

function DeadlineForm({ portal, onSave }: { portal: CompanyPortalRow; onSave: (deadlineLocal: string, autoClose: boolean) => void }) {
  const [value, setValue] = useState(toDatetimeLocalInTz(portal.deadline_at, portal.deadline_timezone))
  const [autoClose, setAutoClose] = useState(portal.deadline_auto_close)

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
      <Field label={`Deadline (${portal.deadline_timezone})`}>
        <Input type="datetime-local" value={value} onChange={(e) => setValue(e.target.value)} />
      </Field>
      <div className="flex items-end pb-0.5">
        <Button size="sm" onClick={() => onSave(value, autoClose)}>
          Save Deadline
        </Button>
      </div>
      <Checkbox
        className="sm:col-span-2"
        label="Auto-close the portal once the deadline passes (uncheck to keep accepting entries past the deadline)"
        checked={autoClose}
        onChange={(e) => {
          setAutoClose(e.target.checked)
          onSave(value, e.target.checked)
        }}
      />
    </div>
  )
}

function GenerateCodeModal({ companyId, onClose, onGenerated }: { companyId: string; onClose: () => void; onGenerated: (code: string) => void }) {
  const [hours, setHours] = useState(3)
  const [maxUses, setMaxUses] = useState<string>('')
  const [editExisting, setEditExisting] = useState(true)
  const [addToEmpty, setAddToEmpty] = useState(true)
  const [deleteGuests, setDeleteGuests] = useState(false)
  const [saving, setSaving] = useState(false)
  const { push } = useToast()

  async function submit() {
    setSaving(true)
    const expiresAt = new Date(Date.now() + hours * 3600_000).toISOString()
    const { data, error } = await supabase.rpc('admin_generate_update_code', {
      p_company_id: companyId,
      p_expires_at: expiresAt,
      p_max_uses: maxUses ? parseInt(maxUses) : null,
      p_permission_edit_existing: editExisting,
      p_permission_add_to_empty: addToEmpty,
      p_permission_delete_guests: deleteGuests,
    })
    setSaving(false)
    if (error) {
      push(parseRpcError(error).code, 'error')
      return
    }
    onGenerated(data as string)
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Generate Temporary Update Code"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} onClick={submit}>
            Generate
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Valid for (hours)">
          <Input type="number" min={1} value={hours} onChange={(e) => setHours(Math.max(1, parseInt(e.target.value) || 1))} />
        </Field>
        <Field label="Maximum uses" hint="Optional — leave blank for unlimited redemptions until it expires.">
          <Input type="number" min={1} value={maxUses} onChange={(e) => setMaxUses(e.target.value)} />
        </Field>
        <div className="space-y-2">
          <Checkbox label="Edit existing guests" checked={editExisting} onChange={(e) => setEditExisting(e.target.checked)} />
          <Checkbox label="Add guests to already-allocated empty slots" checked={addToEmpty} onChange={(e) => setAddToEmpty(e.target.checked)} />
          <Checkbox label="Delete guests" checked={deleteGuests} onChange={(e) => setDeleteGuests(e.target.checked)} />
        </div>
        <p className="text-xs text-slate-500">An update code can never create rooms, change hotels, room types or allocation counts — only touch guests already allocated.</p>
      </div>
    </Modal>
  )
}
